import ticketActions from '../../../../constants/ticketActions';
import useSendTicketUpdateModal from '../../../modals/useSendTicketUpdateModal';
import ticketService from '../../../../ticketService';
import { useSelector } from 'react-redux';
import { constants, getSpecificClientConfig } from 'redux/modules/client-config/selectors';
import useLaunchOfferModal from 'modules/emergencies/components/modals/useLaunchOfferModal';
import t from 'modules/emergencies/components/modals/TicketUpdateTranslations';
import useFinishCostNegotiationModal from 'modules/emergencies/components/modals/useFinishCostNegotiationModal';
import useCloseAuctionsModal from 'modules/emergencies/components/modals/useCloseAuctionsModal';
import usePriceNegotiationPermissions from 'modules/emergencies/permissions/usePriceNegotiationPermissions';
import PRICE_NEGOTIATION_PERMISSIONS_NAMES from 'modules/emergencies/permissions/priceNegotiationPermissionsNames';

const useActions = (ticket) => {
  const onTheWayHandler = useSendTicketUpdateModal(ticket, ticketActions.ON_THE_WAY);
  const delayedHandler = useSendTicketUpdateModal(ticket, ticketActions.DELAYED);
  const deliveredHandler = useSendTicketUpdateModal(ticket, ticketActions.DELIVERED);
  const pickupHandler = useSendTicketUpdateModal(ticket, ticketActions.PICKUP);
  const launchOfferHandler = useLaunchOfferModal(ticket, ticketActions.LAUNCH_OFFER);
  const finishCostNegotiationHandler = useFinishCostNegotiationModal(
    ticket,
    ticketActions.FINISH_COST_NEGOTIATION
  );
  const closeAuctionsHandler = useCloseAuctionsModal(ticket, ticketActions.CLOSE_AUCTIONS);

  const canSendMessage = usePriceNegotiationPermissions(
    PRICE_NEGOTIATION_PERMISSIONS_NAMES.ACTION_SEND_MESSAGE
  );

  let enabledActions = [];

  const isMessagesActionFeatureFlagEnabled = useSelector((state) =>
    getSpecificClientConfig(state, constants.TICKET_LIST_ACTIONS_SEND_MESSAGE_ENABLED)
  );

  const sendMessagesActionsEnabled = canSendMessage && isMessagesActionFeatureFlagEnabled;

  const launchOfferActionEnabled =
    useSelector((state) =>
      getSpecificClientConfig(state, constants.TICKET_LIST_ACTIONS_LAUNCH_OFFER_ENABLED)
    ) && ticketService.bussiness.hasCargo(ticket);

  if (sendMessagesActionsEnabled) {
    enabledActions.push(
      {
        type: ticketActions.PICKUP,
        enabled: ticketService.bussiness.canTicketReceiveUpdate(ticket, ticketActions.PICKUP),
        onClick: pickupHandler,
        disabledTooltip: t.disabledTooltipSendUpdate,
      },
      {
        type: ticketActions.ON_THE_WAY,
        enabled: ticketService.bussiness.canTicketReceiveUpdate(ticket, ticketActions.ON_THE_WAY),
        onClick: onTheWayHandler,
        disabledTooltip: t.disabledTooltipSendUpdate,
      },
      {
        type: ticketActions.DELAYED,
        enabled: ticketService.bussiness.canTicketReceiveUpdate(ticket, ticketActions.DELAYED),
        onClick: delayedHandler,
        disabledTooltip: t.disabledTooltipSendUpdate,
      },
      {
        type: ticketActions.DELIVERED,
        enabled: ticketService.bussiness.canTicketReceiveUpdate(ticket, ticketActions.DELIVERED),
        onClick: deliveredHandler,
        disabledTooltip: t.disabledTooltipSendUpdate,
      }
    );
  }

  const canLaunchOffer = usePriceNegotiationPermissions(
    PRICE_NEGOTIATION_PERMISSIONS_NAMES.ACTION_LAUNCH_OFFER
  );

  const showLaunchOffer = canLaunchOffer && ticketService.bussiness.showLaunchOffer(ticket);

  const canFinishCostNegotiation = usePriceNegotiationPermissions(
    PRICE_NEGOTIATION_PERMISSIONS_NAMES.ACTION_FINISH_COST_NEGOTIATION
  );

  const showFinishCostNegotiation =
    canFinishCostNegotiation && ticketService.bussiness.showFinishCostNegotiation(ticket);

  const canCloseAuctions = usePriceNegotiationPermissions(
    PRICE_NEGOTIATION_PERMISSIONS_NAMES.ACTION_CLOSE_AUCTIONS
  );

  const showCloseAuctions = canCloseAuctions && ticketService.bussiness.showCloseAuctions(ticket);

  const hasBidAction = showLaunchOffer || showFinishCostNegotiation || showCloseAuctions;

  if (sendMessagesActionsEnabled && launchOfferActionEnabled && hasBidAction) {
    enabledActions.push({
      type: ticketActions.SEPARATOR,
    });
  }

  if (launchOfferActionEnabled) {
    if (showLaunchOffer) {
      let disabledTooltipLaunchOffer = t.disabledTooltipLaunchOffer;
      let hasAgent = !!ticket?.assigned_to?.id;
      if (!hasAgent) {
        disabledTooltipLaunchOffer = t.disabledTooltipLaunchOfferNoAgent;
      }
      enabledActions.push({
        type: ticketActions.LAUNCH_OFFER,
        enabled: ticketService.bussiness.canLaunchOffer(ticket),
        onClick: launchOfferHandler,
        disabledTooltip: disabledTooltipLaunchOffer,
      });
    }
    if (showFinishCostNegotiation) {
      enabledActions.push({
        type: ticketActions.FINISH_COST_NEGOTIATION,
        enabled: ticketService.bussiness.canFinishCostNegotiation(ticket),
        onClick: finishCostNegotiationHandler,
        disabledTooltip: t.finishCostNegotiationDisabledTooltip,
      });
    }
    if (showCloseAuctions) {
      enabledActions.push({
        type: ticketActions.CLOSE_AUCTIONS,
        enabled: ticketService.bussiness.canCloseAuctions(ticket),
        onClick: closeAuctionsHandler.openCloseAuctionsModal,
        disabledTooltip: t.closeAuctionsDisabledTooltip,
      });
    }
  }

  if (enabledActions.length === 0) {
    enabledActions.push({
      type: ticketActions.NO_AVAILABLE_ACTIONS,
    });
  }

  return enabledActions;
};

export default useActions;
